/**
 * /brand-match — a brand brief in, ranked shows out.
 *
 * The demand side of the inventory, and the first screen that renders what O3 produces.
 *
 * THE JOB OF THIS PAGE IS TO NOT OVERSTATE. Every number underneath it has been carefully kept
 * honest — a missing view forecast is stored as absent rather than zero, a category adjacency is
 * labelled differently from a real product hit — and a results screen is exactly where that care
 * gets undone by rendering everything as a confident number in a big font. So:
 *
 *   - a category-only match is never shown as a product match, at either level
 *   - "0 impressions" is never printed when the truth is "we have no forecast"
 *   - which signals actually contributed is shown, not buried, because a ranking computed without
 *     the audience component is a different claim from one computed with it
 *   - what the gate removed is shown, because a brand asked for most of it and an unexplained drop
 *     from 124 scenes to 60 looks like a bug
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { Loader2, ChevronDown, ChevronRight, Search } from "lucide-react";
import type {
  BrandMatchResult,
  BriefFieldOptions,
  CraftDistribution,
  MatchedShow,
} from "@/types/brand-match";

const TIER_LABEL: Record<string, string> = {
  product: "Your product is in the scene",
  category_only: "Related category only",
};

/** Bands rather than a raw number in isolation: 74.3 means nothing on its own, and the difference
 *  between 74 and 78 is well inside the noise of a model nobody has back-tested yet. */
function band(score: number): { label: string; className: string } {
  if (score >= 85) return { label: "Strong", className: "bg-emerald-100 text-emerald-800" };
  if (score >= 70) return { label: "Fair", className: "bg-amber-100 text-amber-800" };
  return { label: "Weak", className: "bg-slate-100 text-slate-700" };
}

function ShowCard({ show, craftDistribution }: {
  show: MatchedShow;
  /** Catalogue-wide craft base rate, so a rating can be read against the norm rather than alone. */
  craftDistribution?: CraftDistribution | null;
}) {
  /** This show's craft level as a share of the catalogue, or "" when we cannot say. */
  const craftShare = (level?: string | null): string => {
    const share = level ? craftDistribution?.share?.[level] : undefined;
    return share === undefined ? "" : `${Math.round(share * 100)}%`;
  };

  const [open, setOpen] = useState(show.rank === 1);
  const tone = band(show.fit_score);
  const isProduct = show.evidence_tier === "product";

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">#{show.rank}</span>
              <CardTitle className="truncate text-lg">{show.title || show.show_id}</CardTitle>
              {show.content_rating && <Badge variant="outline">{show.content_rating}</Badge>}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {/* The tier, stated in words. "category_only" as a raw enum reads as a technicality;
                  "related category only" reads as what it is — adjacency, not a hit. */}
              <Badge className={isProduct ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}>
                {TIER_LABEL[show.evidence_tier]}
              </Badge>
              <span className="text-sm text-muted-foreground">
                {show.scene_count} {show.scene_count === 1 ? "placement" : "placements"}
              </span>
            </div>
          </div>
          <div className="shrink-0 text-right">
            <div className="text-2xl font-semibold tabular-nums">{show.fit_score}</div>
            <Badge className={tone.className}>{tone.label} fit</Badge>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {/* Impressions only when there is a forecast. total_forecast_impressions comes back as 0.0
            when no creator in the comparison had one, and printing "0 impressions" would say this
            show has no audience — a claim we have no basis for and that is probably false. */}
        {show.total_forecast_impressions ? (
          <p className="mb-3 text-sm text-muted-foreground">
            {Math.round(show.total_forecast_impressions).toLocaleString()} forecast impressions
            <span className="ml-1 text-xs">
              (across all {show.scene_count} placements — impressions, not unique viewers)
            </span>
          </p>
        ) : (
          <p className="mb-3 text-sm text-muted-foreground">
            {/* NOT "has not connected YouTube" any more. That was already a guess, and it became a
                visible contradiction the moment demographics landed: this line sat directly above a
                measured audience block that could only exist BECAUSE the creator connected YouTube.
                What is actually missing is the impressions figure, so say only that. */}
            No reach forecast yet for this show.
          </p>
        )}

        {/* WHY THIS MATCH — the generated rationale, on the shortlist only. The dominant signal and
            the rating bands behind this prose were computed before the model saw them, so the
            sentences explain the score rather than drifting from it. `via` is shown because a
            reader must be able to tell a written reason from a deterministic stand-in — this text
            could end up quoted to a brand. */}
        {show.rationale && (
          <div className="mb-3 rounded-md border border-border/60 bg-background p-3">
            <p className="text-sm">{show.rationale.reason}</p>
            <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
              <li><span className="font-medium text-foreground">Content — </span>{show.rationale.content_note}</li>
              <li><span className="font-medium text-foreground">Suitability — </span>{show.rationale.suitability_note}</li>
              <li><span className="font-medium text-foreground">Audience — </span>{show.rationale.audience_note}</li>
            </ul>
            <p className="mt-2 text-[11px] text-muted-foreground">
              Ranked mainly on {show.rationale.dominant_signal ?? "content"}
              {show.rationale.via === "heuristic"
                ? " · written without the model"
                : ` · written by ${show.rationale.via}`}
            </p>
          </div>
        )}

        {/* THE CREATOR'S FORECAST, as a range. Showing only the base case would imply a precision
            O1 does not claim — one real show is 1,347 base inside a 490–169,595 band. */}
        {show.details?.forecast && (
          <p className="mb-3 text-sm text-muted-foreground">
            <span className="font-medium text-foreground">
              {Math.round(show.details.forecast.base).toLocaleString()} views
            </span>
            {show.details.forecast.low != null && show.details.forecast.high != null && (
              <> · range {Math.round(show.details.forecast.low).toLocaleString()}–
                {Math.round(show.details.forecast.high).toLocaleString()}</>
            )}
            {show.details.forecast.horizon_days && <> · {show.details.forecast.horizon_days}-day</>}
            {show.details.channel_title && <> · {show.details.channel_title}</>}
          </p>
        )}

        {/* SCOPE HEADER. Everything below describes the WHOLE SHOW; everything above describes what
            matched THIS brief. Without the divider the card reads as one list, and a buyer sees
            "2 placements" beside "of 13 scenes" and reasonably asks which number is wrong.
            Neither is — they answer different questions. */}
        {(show.details?.show_tags || show.details?.moments || show.details?.formats) && (
          <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            About the show — all {show.details?.moments?.scenes ?? ""} scenes, not just the matches
          </p>
        )}

        {/* WHAT THE SHOW IS — the agent's controlled targeting vocabularies. Environments and
            Characters are two of the spec's three M/C/E legs; Moments is the third and is counted
            per scene below. */}
        {show.details?.show_tags && (
          <div className="mb-3 space-y-1.5 text-xs">
            {([
              ["Characters", show.details.show_tags.character_archetypes],
              ["Tone", show.details.show_tags.tonal_qualities],
              ["Genre", show.details.show_tags.genre_focus],
              ["Themes", show.details.show_tags.cultural_markers],
            ] as [string, string[] | null | undefined][])
              .filter(([, values]) => values && values.length)
              .map(([label, values]) => (
                <div key={label} className="flex flex-wrap items-baseline gap-1.5">
                  <span className="w-20 shrink-0 text-muted-foreground">{label}</span>
                  {values!.map((v) => (
                    <span key={v} className="rounded bg-muted px-1.5 py-0.5">{v}</span>
                  ))}
                </div>
              ))}
          </div>
        )}

        {/* CRAFT — how good the script is, assessed once against the Scripted + YouTube rubric.
            NOT PART OF THE RANKING. The spec makes Creative Quality an optional modifier a brand
            switches on per campaign, so this sits beside the fit signals rather than among them.
            Absent for anything analysed before craft reached the placement path — rendered as
            nothing rather than as an average, because "not assessed" and "middling" are different.
            Full detail is admin-side on purpose: the spec says not to expose the methodology or
            thresholds to an advertiser, so an advertiser-facing view should show `overall` alone. */}
        {show.details?.craft?.overall && (
          <div className="mb-3 flex flex-wrap items-baseline gap-1.5 text-xs">
            <span className="w-20 shrink-0 text-muted-foreground">Craft</span>
            <span className={
              show.details.craft.overall === "strong"
                ? "rounded bg-emerald-100 px-2 py-0.5 font-medium text-emerald-900"
                : show.details.craft.overall === "adequate"
                ? "rounded bg-slate-200 px-2 py-0.5 font-medium text-slate-800"
                : "rounded bg-amber-100 px-2 py-0.5 font-medium text-amber-900"
            }>
              {show.details.craft.overall.replace("_", " ")}
            </span>
            {/* THE BASE RATE, beside the rating. Without it "strong" reads as a distinction; with
                it a buyer can see that most assessed scripts are strong and weight it accordingly.
                Absent when nothing has been assessed, rather than shown as 0%. */}
            {craftShare(show.details.craft.overall) && (
              <span className="text-muted-foreground">
                {craftShare(show.details.craft.overall)} of {craftDistribution!.n} assessed
                scripts rate this
              </span>
            )}
            {/* Per dimension, with its note on hover. Only the ones below "strong" are called out
                by weight — a buyer scanning nine shows needs the exceptions, not a full report. */}
            {(["structure", "hook", "characters", "dialogue", "payoff"] as const)
              .map((key) => ({ key, dim: show.details!.craft![key] }))
              .filter(({ dim }) => dim?.level)
              .map(({ key, dim }) => (
                <span key={key}
                      title={dim!.note || undefined}
                      className={dim!.level === "strong"
                        ? "text-muted-foreground"
                        : "font-medium text-amber-800"}>
                  {key} {dim!.level.replace("_", " ")}
                </span>
              ))}
          </div>
        )}

        {/* ENVIRONMENTS — where the show's scenes physically happen, per the spec's vocabulary.
            Replaces the old show-level "Setting", which answered the same question on a superseded
            axis (Suburban vs Home) and was one label for an entire script. */}
        {show.details?.environments && (
          <div className="mb-3 flex flex-wrap items-baseline gap-1.5 text-xs">
            <span className="w-20 shrink-0 text-muted-foreground">Environments</span>
            {Object.entries(show.details.environments.counts).map(([environment, n]) => (
              <span key={environment} className="rounded bg-muted px-1.5 py-0.5">
                {n} × {environment}
              </span>
            ))}
          </div>
        )}

        {/* MOMENTS — counted over distinct scenes, so a scene holding eight products counts once. */}
        {show.details?.moments && (
          <div className="mb-3 flex flex-wrap items-baseline gap-1.5 text-xs">
            <span className="w-20 shrink-0 text-muted-foreground">Moments</span>
            {/* COUNTS, not labels — "4 × Meal" means four of this show's scenes are meals. Written
                with the unit so it cannot be read as a score. */}
            {Object.entries(show.details.moments.counts).map(([moment, n]) => (
              <span key={moment} className="rounded bg-muted px-1.5 py-0.5">
                {n} × {moment}
              </span>
            ))}
            {/* SCENES THAT MATCH NO CHIP, described but visually distinct. About a third of the
                catalogue is Crisis or Confrontation, which the brand-facing vocabulary is right to
                refuse — but a buyer should read what those scenes ARE rather than a gap. Outlined
                rather than filled so a label they cannot brief against never looks like one they
                can. */}
            {show.details.moments.other &&
              Object.entries(show.details.moments.other).map(([moment, n]) => (
                <span key={moment}
                      className="rounded border border-dashed border-muted-foreground/40 px-1.5 py-0.5 text-muted-foreground">
                  {n} × {moment}
                </span>
              ))}
            <span className="text-muted-foreground">
              of {show.details.moments.scenes} scene{show.details.moments.scenes === 1 ? "" : "s"}
            </span>
          </div>
        )}

        {/* FORMAT MIX is also a price — the rate card differs by format, so "41 placements" without
            the split does not tell a buyer what they are buying. */}
        {show.details?.formats && (
          <p className="mb-3 text-xs text-muted-foreground">
            {Object.entries(show.details.formats.by_type)
              .map(([type, n]) => `${n} × ${type}`)
              .join(" · ")}
            {show.details.formats.total_seconds != null &&
              ` · ${show.details.formats.total_seconds}s on screen`}
            {show.details.formats.non_commercial
              ? ` · ${show.details.formats.non_commercial} non-commercial, not sellable`
              : ""}
          </p>
        )}

        {/* THE CREATOR'S MEASURED AUDIENCE. Rendered only when measured — absent, never zero. */}
        {show.demographics && (
          <div className="mb-3 text-xs">
            <div className="flex flex-wrap items-baseline gap-1.5">
              <span className="w-20 shrink-0 text-muted-foreground">Audience</span>
              {show.demographics.gender_distribution &&
                Object.entries(show.demographics.gender_distribution)
                  .sort((a, b) => b[1] - a[1]).slice(0, 2)
                  .map(([g, v]) => (
                    <span key={g} className="rounded bg-muted px-1.5 py-0.5">
                      {g} {Math.round(v * 100)}%
                    </span>
                  ))}
              {show.demographics.age_distribution &&
                Object.entries(show.demographics.age_distribution)
                  .sort((a, b) => b[1] - a[1]).slice(0, 3)
                  .map(([a, v]) => (
                    <span key={a} className="rounded bg-muted px-1.5 py-0.5">
                      {a} {Math.round(v * 100)}%
                    </span>
                  ))}
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">
              {show.demographics.tier === "oauth_distributions"
                ? "Measured from the creator's own YouTube analytics"
                : "Estimated from a coarse audience label"}
              {show.demographics.geo_distribution?.US != null &&
                ` · ${Math.round(show.demographics.geo_distribution.US * 100)}% US`}
            </p>
          </div>
        )}

        {/* AUDIENCE MATCH — measured, not inferred. Rendered only when we actually measured this
            creator: audience is null otherwise, and an absent block is the honest empty state.
            Printing "0% on target" for a creator we never measured would state the opposite of
            what we know, which is the one rule o3_match/audience.py is most insistent about.

            Both numbers are shown because the blend is an UPPER bound: it is a weighted average of
            marginals, so a show can be 60% female and 79% over-35 and still have very few people
            who are both. The joint is the independence product underneath it. Showing only the
            blend would let a buyer read "75% on target" as "75% of viewers match all criteria". */}
        {show.audience && (
          <div className="mb-3 rounded-md border border-border/60 bg-muted/30 p-3">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm font-medium">
                {Math.round(show.audience.on_target_rate * 100)}% on target
              </span>
              {show.audience.channel_demo_lean && (
                <span className="text-xs text-muted-foreground">
                  {show.audience.channel_demo_lean}
                  {show.audience.channel_us_share != null &&
                    ` · ${Math.round(show.audience.channel_us_share * 100)}% US`}
                </span>
              )}
            </div>

            <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
              {Object.entries(show.audience.components).map(([name, value]) => (
                <span key={name}>
                  {name} {Math.round((value as number) * 100)}%
                </span>
              ))}
            </div>

            <p className="mt-1.5 text-xs text-muted-foreground">
              Weighted match on each attribute separately.
              {show.audience.joint_independent != null && (
                <> Roughly {Math.round(show.audience.joint_independent * 100)}% likely match every
                  one at once.</>
              )}
              {show.audience.tier === "oauth_distributions"
                ? " Measured from the creator's own YouTube analytics."
                : " Estimated from a coarse audience label."}
            </p>
          </div>
        )}

        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex items-center gap-1 text-sm font-medium text-slate-700 hover:text-slate-900"
        >
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          {/* "placements", not "moments" — Moment is now a controlled vocabulary term with its own
              chip on every row, and using the same word for "a matched product row" made the
              toggle read as a count of Meals and Celebrations. */}
          {open ? "Hide" : "Show"} the {show.product_placement.length} matched{" "}
          {show.product_placement.length === 1 ? "placement" : "placements"}
        </button>

        {open && (
          <div className="mt-3 space-y-3">
            {show.product_placement.map((p) => (
              <div key={p.placement_id} className="rounded-lg border border-slate-200 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{p.product_placed || "—"}</span>
                    {p.exact_product_match && (
                      <Badge className="bg-emerald-100 text-emerald-800">exact match</Badge>
                    )}
                    {p.integration_format && <Badge variant="outline">{p.integration_format}</Badge>}
                  </div>
                  <span className="text-sm tabular-nums text-muted-foreground">
                    fit {p.fit_score}
                  </span>
                </div>
                {/* MOMENT AND ENVIRONMENT — what this placement IS and WHERE it happens.
                    ON THEIR OWN LINE, not folded into the metadata below. These are the two things
                    a brand briefs against, and buried among scene id, page, tone and IAB category
                    they were six competing items in one muted string — present in the data and
                    invisible on the page. Labelled, because "Home" alone could be read as a
                    product category and "Work" as a job title. */}
                {(p.scene_moment || p.scene_environment) && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
                    {p.scene_moment && (
                      <span className={
                        // Solid when it is one of the spec's ten and a brief can ask for it;
                        // outlined when it is the legacy descriptor, shown so the scene reads as
                        // something rather than as a gap. The two must never look alike.
                        p.scene_moment_matched
                          ? "rounded bg-slate-900 px-2 py-0.5 font-medium text-white"
                          : "rounded border border-dashed border-slate-300 px-2 py-0.5 text-muted-foreground"
                      }>
                        {p.scene_moment}
                      </span>
                    )}
                    {p.scene_environment && (
                      <span className="rounded bg-slate-100 px-2 py-0.5 font-medium text-slate-700">
                        {p.scene_environment}
                      </span>
                    )}
                    {!p.scene_moment_matched && p.scene_moment && (
                      <span className="text-muted-foreground">not a briefable moment</span>
                    )}
                  </div>
                )}
                {/* THE SCREENPLAY'S OWN HEADING, when we have it. It is where the environment chip
                    above is read from, and it tells a buyer where they would actually be buying —
                    "INT. PAULLO HOUSE - KITCHEN" against "SC001", which says nothing. Monospaced
                    because it is verbatim from the script rather than our words about it. */}
                {p.scene_slugline && (
                  <p className="mt-1.5 font-mono text-xs text-slate-600">{p.scene_slugline}</p>
                )}
                <p className="mt-1 text-xs text-muted-foreground">
                  {p.scene_id}
                  {p.page_number ? ` · page ${p.page_number}` : ""}
                  {p.scene_tone ? ` · ${p.scene_tone}` : ""}
                  {p.iab_category ? ` · ${p.iab_category.replace(/^\d+:\s*/, "")}` : ""}
                </p>
                {/* The agent's own words about why this placement is what it is. The most useful
                    thing on the page for a human deciding whether to trust the row. */}
                {p.suitability_reason && (
                  <p className="mt-2 text-sm text-slate-700">{p.suitability_reason}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function BrandMatchPage() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [options, setOptions] = useState<BriefFieldOptions | null>(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<BrandMatchResult | null>(null);

  const [brand, setBrand] = useState("");
  const [productTerms, setProductTerms] = useState("");
  const [targetIab, setTargetIab] = useState("");
  const [description, setDescription] = useState("");
  const [ratingCeiling, setRatingCeiling] = useState("");
  const [excluded, setExcluded] = useState<string[]>([]);
  // The audience target. Optional on purpose: with none of these stated, target_from_brief returns
  // null and every show reports "not measured" rather than a misleading 0%.
  const [audGender, setAudGender] = useState("");
  const [audAge, setAudAge] = useState("");
  const [audInterests, setAudInterests] = useState("");

  useEffect(() => {
    apiClient
      .get<BriefFieldOptions>("/api/v1/brand-match/fields")
      .then(setOptions)
      .catch(() => setOptions(null));
  }, []);

  async function run() {
    if (!brand.trim()) {
      toast({ title: "A brand name is needed", variant: "destructive" });
      return;
    }
    setRunning(true);
    try {
      const body = await apiClient.post<BrandMatchResult>("/api/v1/brand-match", {
        brand,
        product_terms: productTerms,
        target_iab: targetIab,
        match_description: description,
        rating_ceiling: ratingCeiling,
        excluded_categories: excluded.join("; "),
        buy_type: "content",
        aud_gender: audGender,
        aud_age: audAge,
        aud_interests: audInterests,
      });
      setResult(body);
    } catch (error) {
      toast({
        title: "Could not run the match",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setRunning(false);
    }
  }

  const signals = result?.summary.signals_used;

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold">Find shows for a brand</h1>
        <p className="text-muted-foreground">
          Matches a brief against every analysed script in the catalogue.
        </p>
      </div>

      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="brand">Brand</Label>
              <Input id="brand" value={brand} onChange={(e) => setBrand(e.target.value)}
                     placeholder="Apple" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <select
                id="category"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={targetIab}
                onChange={(e) => setTargetIab(e.target.value)}
              >
                <option value="">No preference</option>
                {(options?.target_iab || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="products">Products to place</Label>
            <Input id="products" value={productTerms} onChange={(e) => setProductTerms(e.target.value)}
                   placeholder="phone; laptop; smart watch" />
            {/* The single most useful instruction on this page. A brief written in brand names
                ("iPhone") misses scenes written in nouns ("cell phone"), and on a real Apple brief
                that alone moved the top show from 76.7 to 83.4. */}
            <p className="text-xs text-muted-foreground">
              Separate with semicolons, and use the words a <em>script</em> would use rather than the
              brand&rsquo;s own — &ldquo;phone&rdquo;, not &ldquo;iPhone&rdquo;.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">The kind of moment to be in</Label>
            <Textarea id="description" rows={3} value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Warm family kitchen scenes. Natural light, candid moments." />
          </div>

          {/* WHO THEY WANT TO REACH. Drives the on-target rate on each result card, measured from
              the creator's own YouTube analytics. Leave blank and no show reports one — an
              unmeasured audience must not render as a low score. */}
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="aud-gender">Audience gender</Label>
              <select
                id="aud-gender"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={audGender}
                onChange={(e) => setAudGender(e.target.value)}
              >
                <option value="">No preference</option>
                <option value="female">Mostly female</option>
                <option value="male">Mostly male</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="aud-age">Audience age</Label>
              <select
                id="aud-age"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={audAge}
                onChange={(e) => setAudAge(e.target.value)}
              >
                {/* YOUTUBE'S OWN BUCKETS. These are the units the measurement comes in, so a brief
                    can only be scored precisely if it speaks them: asking for "35-44" used to be
                    scored on everything from 35 up, which on a real channel read 50% as 79%.
                    "35 and over" is kept because plenty of briefs are written that way, and it
                    resolves to every bucket at or above it. */}
                <option value="">No preference</option>
                <option value="13-17">13–17</option>
                <option value="18-24">18–24</option>
                <option value="25-34">25–34</option>
                <option value="35-44">35–44</option>
                <option value="45-54">45–54</option>
                <option value="55-64">55–64</option>
                <option value="65-">65+</option>
                <option value="18-34">18–34 (spans two buckets)</option>
                <option value="25-44">25–44 (spans two buckets)</option>
                <option value="35+">35 and over</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="aud-interests">Audience interests</Label>
              <Input id="aud-interests" value={audInterests}
                     onChange={(e) => setAudInterests(e.target.value)}
                     placeholder="clean; healthy; family" />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="ceiling">Highest content rating allowed</Label>
              <select
                id="ceiling"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={ratingCeiling}
                onChange={(e) => setRatingCeiling(e.target.value)}
              >
                {/* No ceiling is the default on purpose: the gate should only remove what a brand
                    actually asked to remove, so silence is not treated as caution. */}
                <option value="">No limit</option>
                {(options?.rating_ceiling || []).map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Never appear beside</Label>
              <div className="flex flex-wrap gap-2 pt-1">
                {(options?.excluded_categories || []).map((c) => {
                  const on = excluded.includes(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() =>
                        setExcluded(on ? excluded.filter((x) => x !== c) : [...excluded, c])
                      }
                      className={`rounded-full border px-3 py-1 text-sm ${
                        on ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 text-slate-700"
                      }`}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <Button onClick={run} disabled={running} className="w-full md:w-auto">
            {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
            {running ? "Matching…" : "Find shows"}
          </Button>
        </CardContent>
      </Card>

      {result && (
        <>
          <Card>
            <CardContent className="space-y-3 pt-6">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-lg font-medium">
                  {result.summary.verdict === "PRODUCT_MATCH" && "Found shows with your product in them"}
                  {result.summary.verdict === "CATEGORY_ONLY" && "No exact product match"}
                  {result.summary.verdict === "NO_MATCH" && "Nothing matched"}
                  {result.summary.verdict === "NO_INVENTORY" && "There is no analysed inventory yet"}
                </span>
                <Badge variant="outline">{result.shows.length} shows</Badge>
              </div>
              {result.summary.verdict_detail && (
                <p className="text-sm text-muted-foreground">{result.summary.verdict_detail}</p>
              )}

              {/* What the gate removed. A brand asked for most of it, and an unexplained drop from
                  124 scenes to 60 otherwise looks like something broke. */}
              {!!result.summary.gated_out && (
                <p className="text-sm text-muted-foreground">
                  {result.summary.scenes_scored} placements considered,{" "}
                  {result.summary.gated_out} removed
                  {result.summary.gate_breakdown && (
                    <> — {Object.entries(result.summary.gate_breakdown)
                      .map(([reason, n]) => `${n} ${reason.replace(/-/g, " ")}`)
                      .join(", ")}</>
                  )}
                  .
                </p>
              )}

              {/* Which signals were live. Shown rather than hidden because a ranking computed
                  without the audience component is a different claim from one computed with it —
                  and it is off whenever any creator in the catalogue has not connected YouTube. */}
              {signals && !signals.reach && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  Ranked on content fit and brand suitability only. Audience size was left out
                  because not every show has a view forecast — including it would rank a creator
                  who has not connected YouTube below one with a tiny following.
                </div>
              )}
            </CardContent>
          </Card>

          <Separator />

          <div className="space-y-4">
            {result.shows.map((s) => (
              <ShowCard key={s.show_id} show={s}
                        craftDistribution={result.summary.craft_distribution} />
            ))}
          </div>

          {result.shows.length === 0 && (
            <p className="py-8 text-center text-muted-foreground">
              Nothing survived the filters. Try relaxing the content rating or the exclusions.
            </p>
          )}
        </>
      )}
    </div>
  );
}
